// deps
import React, { memo, ReactNode, ReactElement, useCallback, useRef, useContext, useMemo } from "react";
import classNames from "classnames";

// internal-deps
import { format } from "@swiggy-private/core-ui";
import {
    CheckoutContext,
    CheckoutActionTypes,
    useCustomHardwareBack,
    CheckoutRoute,
    CouponListingActionPayload,
    isJioHotstarFlow,
} from "@swiggy-private/checkout-core";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// context
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
// components
import CommonUIWrapper from "@cart-client/components/common-ui-wrapper/CommonUIWrapper";

// styles
import styles from "./CouponUIWrapper.scss";

// utils
import { isEmpty } from "@cart-client/utils/Core";
import { shouldSkipCartReloadOnRedirection } from "@cart-client/helpers/CouponListing";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

// constants
import { APPLY_COUPON_TEXTS } from "@cart-client/constants/Coupon";

interface CouponUIWrapperProps {
    children: ReactNode;
    containerId?: string;
    cartTotal?: string;
    isLoading: boolean;
    isPaymentCouponListingPage?: boolean;
    isGiftingCartEmpty?: boolean;
}

export interface ScrollableDivElement extends HTMLDivElement {
    getScrollPosition: () => number;
}

const CouponHeaderContent = (props: {
    subTitle?: string;
    title: string;
    isGiftingCouponListing?: boolean;
    isGiftingCartEmpty?: boolean;
}): ReactElement | null => {
    const { title, subTitle, isGiftingCouponListing, isGiftingCartEmpty = false } = props;

    if (isGiftingCouponListing && isGiftingCartEmpty) {
        return null;
    }

    return (
        <div className={styles.containerHeaderWrapper} tabIndex={-1}>
            <Text color="text_Highest_Emphasis" variant="V2.body1.neutral.bold" asElement="h1">
                {title}
            </Text>
            {subTitle && (
                <Text
                    className={styles.containerHeaderSubtitle}
                    asElement="h2"
                    color="text_Med_Emphasis"
                    variant="V2.body3.neutral.regular"
                >
                    {subTitle}
                </Text>
            )}
        </div>
    );
};

const CouponUIWrapper = ({
    children,
    cartTotal,
    isLoading,
    isPaymentCouponListingPage,
    ...props
}: CouponUIWrapperProps): React.ReactElement => {
    const { containerId } = props;
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { data } = useContext(CartFlowContext);
    const isGiftingCouponListing = isGiftingCartSource(data?.cartSource);

    const _onBack = useCallback(() => {
        // Skip reloading cart if the user is clicking on back within n seconds of coming to couponlisting
        const skipCartReload = shouldSkipCartReloadOnRedirection(
            (checkoutState?.payload as CouponListingActionPayload)?.couponListingLaunchTimestamp,
        );

        if (isGiftingCouponListing) {
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_GIFTING_CART,
                payload: {
                    skipCartReload,
                },
            });
        } else {
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_CART,
                payload: {
                    skipCartReload,
                },
            });
        }
    }, [checkoutState?.payload, isGiftingCouponListing, setCheckoutState]);

    useCustomHardwareBack([CheckoutRoute.COUPON_LISTING, CheckoutRoute.GIFTING_COUPON_LISTING], _onBack);

    const containerRef = useRef<ScrollableDivElement>(null);

    const containerClass = classNames(styles.container, {
        [styles.containerLockScroll]: isLoading,
        [styles.containerGiftingCart]: isGiftingCouponListing && !props.isGiftingCartEmpty,
    });

    const headerTitle = useMemo(() => {
        if (isEmpty(Number(cartTotal))) {
            return "";
        }
        const subtitleText = isPaymentCouponListingPage
            ? APPLY_COUPON_TEXTS.TOTAL_PAYABLE_SUB_TITLE
            : APPLY_COUPON_TEXTS.SUB_TITLE;
        return format(subtitleText, cartTotal || "");
    }, [cartTotal, isPaymentCouponListingPage]);

    return (
        <CommonUIWrapper
            onBack={_onBack}
            header={
                <CouponHeaderContent
                    title={
                        isPaymentCouponListingPage
                            ? APPLY_COUPON_TEXTS.PAYMENT_COUPONS_PAGE_TITLE
                            : APPLY_COUPON_TEXTS.TITLE
                    }
                    subTitle={headerTitle}
                    isGiftingCouponListing={isGiftingCouponListing}
                    isGiftingCartEmpty={props.isGiftingCartEmpty}
                />
            }
            content={children}
            key={0}
            ref={containerRef}
            containerId={containerId}
            containerCSSClass={containerClass}
            headerCSSClass={styles.containerHeader}
            isPageScrolled={true}
            isJioHotstarFlow={isJioHotstarFlow()}
        />
    );
};

export default memo(CouponUIWrapper);
